import React from "react";
import { IntlShape } from "react-intl";
import { RouterChildContext } from "react-router";
import axios from "axios";

import { translateMessage } from "src/app/methods/translateMessage";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { Metadata } from "src/app/Task/Subtask/Subtask";
import { ProjectTemplate, ProjectTemplateType } from "src/redux/project/types";
import { ProjectSetupOption, ProjectSetupType, SelectOption } from "./types";
import { UploadMultiFiles } from "src/app/libs/uploadMultiFiles";
import {
  MAX_SIZE_SINGLE_ATTACHMENT,
  ParentType,
  MAX_SIZE_PROJECT_COVER,
} from "src/utils/uploadFunctionsUtils";
import { API_URLS } from "src/utils/API_URLS";
import { handleError } from "src/utils/error";
import ValidateFiles from "src/app/libs/validateFiles";
import AwsFileUpload from "src/app/libs/awsFileUpload";
import { ApiResponse, CampaignTemplate, Token } from "src/types";
import { NewProjectBriefData } from "src/redux/main/mainReducer";
import { getUrlWithQueryParams } from "src/utils/ROUTES";
import { UploadFile } from "src/app/libs/uploadFile";
import { showErrorToast } from "src/utils/methods";
import { type AnalyzedBrief } from "src/types";

export const getProjectSetupOptions = (
  intl: IntlShape,
): ProjectSetupOption[] => {
  return [
    {
      value: ProjectSetupType.Template,
      label: translateMessage({
        intl,
        id: "ws_template",
        defaultMessage: "Template",
      }),
      customElement: (
        <div className="ws-react-select__option-description">
          <IDHFormattedMessage
            id="ws_campaign_from_template"
            defaultMessage="Create a new campaign based on pre-defined template"
          />
        </div>
      ),
    },
    {
      value: ProjectSetupType.BasedOnExistingProject,
      label: translateMessage({
        intl,
        id: "ws_use_structure_from_existing_campaign",
        defaultMessage: "Use structure from existing campaign",
      }),
      customElement: (
        <div className="ws-react-select__option-description">
          <IDHFormattedMessage
            id="ws_campaign_based_on_existing_campaign"
            defaultMessage="Create a new campaign based on the structure of an existing campaign"
          />
        </div>
      ),
    },
    {
      value: ProjectSetupType.Blank,
      label: translateMessage({
        intl,
        id: "ws_blank",
        defaultMessage: "Blank",
      }),
      customElement: (
        <div className="ws-react-select__option-description">
          <IDHFormattedMessage
            id="ws_campaign_without_structure"
            defaultMessage="Create a new campaign without structure"
          />
        </div>
      ),
    },
  ];
};

export const getProjectTemplateOptions = (
  projectTemplates: ProjectTemplate[],
  type: ProjectTemplateType,
) => {
  return projectTemplates
    .filter((projectTemplate) => projectTemplate.type === type)
    .map((projectTemplate) => {
      const baseOptionProperties = {
        value: projectTemplate.uuid,
        label: projectTemplate.name,
      };

      if (projectTemplate.description) {
        return {
          ...baseOptionProperties,
          customElement: (
            <div className="ws-react-select__option-description">
              {projectTemplate.description}
            </div>
          ),
        };
      }

      return baseOptionProperties;
    });
};

const removeDuplicatedFields = (arr: Metadata[]) => {
  const sanitizedArray = arr.reduce(
    (uniqueObjects: Metadata[], currentObject) => {
      const existingObject = uniqueObjects.find(
        (obj) => obj.uuid === currentObject.uuid,
      );
      if (!existingObject) {
        uniqueObjects.push(currentObject);
      }

      return uniqueObjects;
    },
    [],
  );

  return sanitizedArray;
};

export const getProjectTemplateFields = (
  selectedBaseTemplateOption: SelectOption | null,
  selectedSocialPlatformTemplateOptions: SelectOption[],
  projectTemplates: ProjectTemplate[],
) => {
  const collectFields = (templateOption: SelectOption) => {
    const foundProjectTemplate = projectTemplates.find(
      (projectTemplate) => projectTemplate.uuid === templateOption.value,
    );
    if (foundProjectTemplate) {
      return [...foundProjectTemplate.projectMetaFields];
    }
    return [];
  };

  let fields: Metadata[] = [];

  if (selectedBaseTemplateOption) {
    fields = fields.concat(collectFields(selectedBaseTemplateOption));
  }

  if (selectedSocialPlatformTemplateOptions.length) {
    selectedSocialPlatformTemplateOptions.forEach((templateOption) => {
      fields = fields.concat(collectFields(templateOption));
    });
  }

  return removeDuplicatedFields(fields);
};

export const isSubmissionFormEnabled = (
  selectedBaseTemplateOption: SelectOption | null,
  selectedSocialPlatformTemplateOptions: SelectOption[],
  projectTemplates: ProjectTemplate[],
) => {
  const result = [];

  if (selectedBaseTemplateOption) {
    const foundBaseTemplate = projectTemplates.find(
      (template) => template.uuid === selectedBaseTemplateOption.value,
    );
    if (foundBaseTemplate) {
      result.push(foundBaseTemplate.isSubmissionFormEnabled);
    }
  }

  if (selectedSocialPlatformTemplateOptions.length) {
    selectedSocialPlatformTemplateOptions.forEach((socialTemplate) => {
      const foundTemplate = projectTemplates.find(
        (projectTemplate) => projectTemplate.uuid === socialTemplate.value,
      );
      if (foundTemplate) {
        result.push(foundTemplate.isSubmissionFormEnabled);
      }
    });
  }

  return result.some(Boolean);
};

export const createNewProjectActionValue = "createNewProject" as const;

export const openCreateNewProjectPane = (
  history: RouterChildContext["router"]["history"],
) => {
  const { location } = history;
  const nextParams = new URLSearchParams(location.search);
  nextParams.set("action", createNewProjectActionValue);

  const newUrl = `${location.pathname}?${nextParams.toString()}`;
  history.push(newUrl);
};

export const closeCreateNewProjectPane = (
  history: RouterChildContext["router"]["history"],
) => {
  const { location } = history;
  const nextParams = new URLSearchParams(location.search);
  const currentAction = nextParams.get("action");

  if (currentAction === createNewProjectActionValue) {
    nextParams.delete("action");
  }

  const newSearch = nextParams.toString();
  const newUrl = newSearch
    ? `${location.pathname}?${newSearch}`
    : location.pathname;

  history.replace(newUrl);
};

export const SUPPORTED_FILE_TYPES_FOR_BRIEF = ["pdf", "docx", "txt"];

export const uploadBriefForExistingProject = async ({
  file,
  wsMetaFieldUuid,
  wsProjectUuid,
  wsWorkspaceUuid,
}: {
  file: File;
  wsMetaFieldUuid: string;
  wsProjectUuid: string;
  wsWorkspaceUuid: string;
}): Promise<AnalyzedBrief | undefined> => {
  const uploadFile = new UploadMultiFiles({
    files: [file],
    allowedTypes: SUPPORTED_FILE_TYPES_FOR_BRIEF,
    maxSize: MAX_SIZE_SINGLE_ATTACHMENT,
    presignedUploadRequestUrl: API_URLS.presignedRequestForProjectMetaField,
    parentType: ParentType.wsProjectMetaField,
    parentUuid: wsMetaFieldUuid,
    wsWorkspaceUuid,
    wsProjectUuid,
    createAssetsUrl: API_URLS.createAiBrief,
  });

  try {
    uploadFile.validate();
    const assets = await uploadFile.createDataStructureForAssets();

    const response =
      await uploadFile.createAssets<ApiResponse<AnalyzedBrief>>(assets);

    return response?.data.content;
  } catch (err) {
    handleError(err);
    console.error("uploadBriefForResearchProject error: ", err);
  }
};

export const uploadBriefForNewProject = async (
  file: File,
  wsWorkspaceUuid: string,
) => {
  try {
    const uncheckedFile = new ValidateFiles({
      files: [file],
      allowedTypes: SUPPORTED_FILE_TYPES_FOR_BRIEF,
      maxSize: MAX_SIZE_SINGLE_ATTACHMENT,
    });
    uncheckedFile.validate();

    const uploadFile = new AwsFileUpload({
      file,
      presignedUploadRequestUrl: API_URLS.presignedUploadRequestTemporaryFile,
    });

    const presignedRequest =
      await uploadFile.sendPresignedUploadRequest<Token>();

    const key = presignedRequest.data.content.requestInputs.key;
    const fileName = file.name;

    await uploadFile.sendAttachmentsToS3({
      token: presignedRequest.data.content,
    });

    const response = await axios.post<ApiResponse<AnalyzedBrief>>(
      API_URLS.analyzeBriefBeforeProjectCreated,
      {
        wsWorkspaceUuid,
        key,
        fileName,
      },
    );

    return {
      key,
      ...(response?.data?.content ?? {}),
    };
  } catch (err) {
    console.error("uploadBriefForResearchProject error: ", err);
    handleError(err);
  }
};

export const isDataInBrief = (data: NewProjectBriefData | null) => {
  if (!data) return false;
  return Object.values(data).some((value) => value.length > 0);
};

export const uploadCoverForNewProject = async (
  file: File,
  projectId: string,
  activeWorkspaceUuid: string,
) => {
  const uploadFile = new UploadFile(
    file,
    [],
    MAX_SIZE_PROJECT_COVER,
    getUrlWithQueryParams(API_URLS.presignedRequestForProjectCover, {
      wsProjectUuid: projectId,
      wsWorkspaceUuid: activeWorkspaceUuid,
    }),
    projectId,
    "ws_project",
    activeWorkspaceUuid,
  );

  try {
    await uploadFile.sendAllRequests();
  } catch (err) {
    console.error("uploadCoverForNewProject error: ", err);
    showErrorToast({
      id: "ws_an_error_occurred_when_uploading_the_file",
      defaultMessage: "An error occurred when uploading the file.",
    });
  }
};

export const uploadCoverForResearchProject = async (file: File) => {
  try {
    const uncheckedFile = new ValidateFiles({
      files: [file],
      allowedTypes: [],
      maxSize: MAX_SIZE_PROJECT_COVER,
    });
    uncheckedFile.validateSize();

    const uploadFile = new AwsFileUpload({
      file,
      presignedUploadRequestUrl: API_URLS.presignedUploadRequestTemporaryFile,
    });

    const presignedRequest =
      await uploadFile.sendPresignedUploadRequest<Token>();

    const key = presignedRequest.data.content.requestInputs.key;
    const fileName = file.name;

    await uploadFile.sendAttachmentsToS3({
      token: presignedRequest.data.content,
    });

    return {
      key,
      fileName,
    };
  } catch (err) {
    console.error("uploadCoverForResearchProject error: ", err);
    handleError(err);
    throw err;
  }
};

export const updateProjectTemplatesWithAITemplates = (
  projectTemplates: ProjectTemplate[],
  aiTemplates: CampaignTemplate[],
) => {
  return projectTemplates.map((template) => {
    const foundTemplate = aiTemplates.find(
      (aiTemplate) => aiTemplate.template_uuid === template.uuid,
    );
    if (foundTemplate && foundTemplate.fields.length > 0) {
      return {
        ...template,
        projectMetaFields: template.projectMetaFields.map((field) => {
          const foundField = foundTemplate.fields.find(
            (aiField) => aiField.field_uuid === field.uuid,
          );
          if (foundField) {
            return {
              ...field,
              defaultValue: foundField.value,
            };
          } else {
            return field;
          }
        }),
      };
    } else {
      return template;
    }
  });
};
